import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useOrg } from "@/stores/org";
import { useSession } from "@/stores/session";
import { fetchMemberships, fetchModules, type Membership } from "./api";

export function useMemberships() {
  const authenticated = useSession((s) => s.status === "authenticated");

  return useQuery({
    queryKey: ["memberships"],
    queryFn: fetchMemberships,
    enabled: authenticated,
    staleTime: 5 * 60_000,
  });
}

/**
 * Organizacion activa, con eleccion automatica.
 *
 * Si la guardada ya no esta entre las membresias — le quitaron el acceso, o
 * cambio de equipo — se cae a la primera disponible en lugar de dejar el panel
 * consultando una organizacion que RLS ya no le deja ver.
 */
export function useActiveOrg(): {
  org: Membership | null;
  memberships: Membership[];
  isPending: boolean;
} {
  const { data, isPending } = useMemberships();
  const activeOrgId = useOrg((s) => s.activeOrgId);
  const setActiveOrg = useOrg((s) => s.setActiveOrg);

  const memberships = data ?? [];
  const current = memberships.find((m) => m.organizationId === activeOrgId) ?? null;

  useEffect(() => {
    if (isPending || memberships.length === 0) return;
    if (!current) setActiveOrg(memberships[0]!.organizationId);
  }, [isPending, current, memberships, setActiveOrg]);

  return { org: current, memberships, isPending };
}

export function useModules(organizationId: string | null) {
  return useQuery({
    queryKey: ["modules", organizationId],
    queryFn: () => fetchModules(organizationId!),
    enabled: Boolean(organizationId),
    staleTime: 5 * 60_000,
  });
}

/** Un modulo apagado no existe: la ruta responde 404, no 403. */
export function useHasModule(key: string, organizationId: string | null): boolean {
  const { data } = useModules(organizationId);
  return (data ?? []).some((m) => m.key === key);
}
